import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { HighlightChessboard, ThemeProvider } from 'react-chess-core';
import { findPlyIndexForFen } from 'react-chess-replay-trainer';
import LayeredAnimatedPreviewBoard from '../common/LayeredAnimatedPreviewBoard';
import { usePreviewBoardVisibility } from '../common/usePreviewBoardVisibility';
import { nonInteractivePreviewBoardShellSx } from '../common/previewBoardShell';
import { useFittedBoardWidth } from '../common/useFittedBoardWidth';
import { fenLineFromOpeningSans } from 'react-chess-core';
import { fetchReplayGame } from './replayApi';
import { useUserSettings } from '../settings/useUserSettings';
import { REPLAY_AUTOPLAY_SPEED_MS } from '../settings/replayAutoplaySpeed';
import GameReviewAutoplayBoard from '../games/shared/GameReviewAutoplayBoard';
import {
  fetchMyGameDetail,
  type AnalyzedMovePly,
  type MistakePly,
} from '../games/my-games/myGamesApi';

const MOVE_MS = 650;
const LOOP_PAUSE_MS = 900;
const PREVIEW_BOARD_MAX_WIDTH = 400;
const PREVIEW_BOARD_MAX_WIDTH_WIDE = 480;
const PREVIEW_BOARD_MAX_WIDTH_ULTRA = 560;
const PREVIEW_BOARD_MAX_WIDTH_MOBILE = 280;

type ReviewPreviewData = {
  movesUci: string[];
  mistakes: MistakePly[];
  analyzedMoves: AnalyzedMovePly[];
  userColor: 'w' | 'b' | null;
};

type ReplayGamePreviewBoardProps = {
  gameId: string;
  openingSans?: string[];
  /** When set, autoplay loops from this position instead of the game start. */
  startFen?: string;
  boardWidth?: number;
  fillContainer?: boolean;
  startDelayMs?: number;
  /** Slide pieces between plies. Disable for fixed-position hover popups. */
  animate?: boolean;
};

const ReplayGamePreviewBoard = ({
  gameId,
  openingSans,
  startFen,
  boardWidth: boardWidthProp,
  fillContainer = false,
  startDelayMs = 0,
  animate = true,
}: ReplayGamePreviewBoardProps) => {
  const visibilityRef = useRef<HTMLDivElement>(null);
  const { animationsActive } = usePreviewBoardVisibility(visibilityRef);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isUltraWide = useMediaQuery(theme.breakpoints.up('xxxl'));
  const isWideDesktop = useMediaQuery(theme.breakpoints.up('xxl'));
  const { settings } = useUserSettings();
  const autoplayStepMs =
    REPLAY_AUTOPLAY_SPEED_MS[settings.replayAutoplaySpeed] ?? 500;
  const [movesSan, setMovesSan] = useState<string[] | undefined>(
    startFen ? undefined : openingSans,
  );
  const [movesUci, setMovesUci] = useState<string[] | undefined>();
  const [reviewPreview, setReviewPreview] = useState<ReviewPreviewData | null>(
    null,
  );
  const openingSansKey = openingSans?.join('\0') ?? '';
  const maxBoardWidth = isMobile
    ? PREVIEW_BOARD_MAX_WIDTH_MOBILE
    : isUltraWide
      ? PREVIEW_BOARD_MAX_WIDTH_ULTRA
      : isWideDesktop
        ? PREVIEW_BOARD_MAX_WIDTH_WIDE
        : PREVIEW_BOARD_MAX_WIDTH;
  const { containerRef, boardWidth: fittedBoardWidth } = useFittedBoardWidth(
    maxBoardWidth,
    { mobileMaxBoardWidth: PREVIEW_BOARD_MAX_WIDTH_MOBILE },
  );
  const boardWidth = fillContainer
    ? fittedBoardWidth
    : (boardWidthProp ?? maxBoardWidth);
  const autoplayActive = animate && animationsActive;

  useEffect(() => {
    setMovesSan(startFen ? undefined : openingSans);
    setMovesUci(undefined);
    setReviewPreview(null);
    // openingSansKey tracks openingSans contents without array-reference churn.
  }, [gameId, openingSansKey, startFen]);

  useEffect(() => {
    let cancelled = false;

    void fetchReplayGame(gameId).then((game) => {
      if (cancelled || !game?.movesSan?.length) {
        return;
      }
      setMovesSan(game.movesSan);
      setMovesUci(game.movesUci);
    });

    return () => {
      cancelled = true;
    };
  }, [gameId]);

  useEffect(() => {
    let cancelled = false;

    void fetchMyGameDetail(gameId)
      .then((detail) => {
        if (cancelled) {
          return;
        }
        if (
          detail.analysisStatus !== 'ready' ||
          detail.game.movesUci.length === 0
        ) {
          return;
        }
        setReviewPreview({
          movesUci: detail.game.movesUci,
          mistakes: detail.mistakes,
          analyzedMoves: detail.analyzedMoves,
          userColor: detail.userColor,
        });
      })
      .catch(() => {
        // Not an analyzed my-game preview; keep the generic replay loop.
      });

    return () => {
      cancelled = true;
    };
  }, [gameId]);

  const startPlyIndex = useMemo(() => {
    const line = reviewPreview?.movesUci ?? movesUci;
    if (!startFen?.trim() || !line?.length) {
      return 0;
    }
    return findPlyIndexForFen(line, startFen);
  }, [movesUci, reviewPreview?.movesUci, startFen]);

  const allFens = useMemo(
    () => (movesSan?.length ? fenLineFromOpeningSans(movesSan) : []),
    [movesSan],
  );

  const fens = useMemo(() => {
    if (allFens.length > 0) {
      const fromPosition = allFens.slice(startPlyIndex);
      return fromPosition.length > 0 ? fromPosition : allFens;
    }
    if (startFen?.trim()) {
      return [startFen];
    }
    return [];
  }, [allFens, startFen, startPlyIndex]);
  const [ply, setPly] = useState(0);

  useEffect(() => {
    setPly(0);
  }, [fens]);

  useEffect(() => {
    if (reviewPreview || !autoplayActive || fens.length <= 1) {
      return;
    }

    let cancelled = false;
    let plyLocal = 0;
    let timeoutId = 0;

    const step = () => {
      if (cancelled) {
        return;
      }

      const next = (plyLocal + 1) % fens.length;
      plyLocal = next;
      setPly(plyLocal);

      const delay = next === 0 ? LOOP_PAUSE_MS : MOVE_MS;
      timeoutId = window.setTimeout(step, delay);
    };

    timeoutId = window.setTimeout(step, startDelayMs + MOVE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [autoplayActive, fens, reviewPreview, startDelayMs]);

  useEffect(() => {
    if (reviewPreview || animate || !animationsActive || fens.length <= 1) {
      return;
    }

    let cancelled = false;
    let plyLocal = 0;
    let timeoutId = 0;

    const step = () => {
      if (cancelled) {
        return;
      }

      plyLocal = (plyLocal + 1) % fens.length;
      setPly(plyLocal);

      const delay = plyLocal === 0 ? LOOP_PAUSE_MS : MOVE_MS;
      timeoutId = window.setTimeout(step, delay);
    };

    timeoutId = window.setTimeout(step, startDelayMs + MOVE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [animate, animationsActive, fens, reviewPreview, startDelayMs]);

  const wrapWithVisibility = (content: React.ReactNode) => {
    if (fillContainer) {
      return (
        <Box
          ref={visibilityRef}
          sx={{ width: '100%', maxWidth: maxBoardWidth }}
        >
          {content}
        </Box>
      );
    }

    return (
      <Box ref={visibilityRef} sx={{ display: 'inline-block' }}>
        {content}
      </Box>
    );
  };

  if (reviewPreview) {
    const reviewBoard = (
      <GameReviewAutoplayBoard
        gameId={gameId}
        movesUci={reviewPreview.movesUci}
        mistakes={reviewPreview.mistakes}
        analyzedMoves={reviewPreview.analyzedMoves}
        userColor={reviewPreview.userColor}
        boardWidth={boardWidth}
        boardTheme={settings.boardTheme}
        autoplayStepMs={autoplayStepMs}
        loop
        startPlyIndex={startPlyIndex}
        startDelayMs={startDelayMs}
        autoplayEnabled={autoplayActive}
      />
    );

    return fillContainer
      ? wrapWithVisibility(
          <Box ref={containerRef} sx={{ width: '100%' }}>
            {reviewBoard}
          </Box>,
        )
      : wrapWithVisibility(reviewBoard);
  }

  const fen = fens[ply];

  const boardShellSx = nonInteractivePreviewBoardShellSx(boardWidth);

  const boardContent = !fen ? (
    <Box
      aria-hidden
      sx={{
        ...boardShellSx,
        bgcolor: 'action.hover',
      }}
    />
  ) : animate && autoplayActive && fens.length > 1 ? (
    <Box sx={boardShellSx}>
      <LayeredAnimatedPreviewBoard
        fens={fens}
        ply={ply}
        boardWidth={boardWidth}
        boardOrientation="white"
        boardTheme={settings.boardTheme}
      />
    </Box>
  ) : (
    <Box sx={boardShellSx}>
      <ThemeProvider theme="dark" boardTheme={settings.boardTheme}>
        <HighlightChessboard
          position={fen}
          boardWidth={boardWidth}
          boardOrientation="white"
          arePiecesDraggable={false}
          showBoardNotation={false}
          animationDuration={0}
          checkSquare=""
          hintSquare={null}
          incorrectMoveSquare={null}
        />
      </ThemeProvider>
    </Box>
  );

  if (fillContainer) {
    return wrapWithVisibility(
      <Box ref={containerRef} sx={{ width: '100%' }}>
        {boardContent}
      </Box>,
    );
  }

  return wrapWithVisibility(boardContent);
};

export default ReplayGamePreviewBoard;
